import React, { useState, useEffect } from 'react';
import { myTournamentsAPI, exportAPI, familyArchersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, getStatusColor, getOrgColor, getDaysUntil } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Clock,
  Download,
  Loader2,
  Users,
  User
} from 'lucide-react';
import { toast } from 'sonner';

export default function CalendarPage() {
  const { user } = useAuth();
  const isParent = user?.role === 'parent';
  const [tournaments, setTournaments] = useState([]);
  const [familyArchers, setFamilyArchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [filterArcherId, setFilterArcherId] = useState('ALL');

  useEffect(() => {
    fetchTournaments();
    if (isParent) {
      fetchFamilyArchers();
    }
  }, [isParent]);

  useEffect(() => {
    // Re-fetch tournaments when archer filter changes
    if (isParent) {
      fetchTournaments();
    }
  }, [filterArcherId, isParent]);

  const fetchFamilyArchers = async () => {
    try {
      const response = await familyArchersAPI.getAll();
      setFamilyArchers(response.data);
    } catch (error) {
      console.error('Failed to fetch family archers');
    }
  };

  const fetchTournaments = async () => {
    try {
      const archerId = filterArcherId !== 'ALL' ? filterArcherId : null;
      const response = await myTournamentsAPI.getAll(archerId);
      setTournaments(response.data);
    } catch (error) {
      toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  // Export current month as PDF
  const handleExportMonth = async () => {
    setExporting(true);
    try {
      const response = await exportAPI.getCalendarPDF({
        month: currentMonth.getMonth() + 1,
        year: currentMonth.getFullYear()
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const monthStr = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).replace(' ', '_');
      a.download = `calendar_${monthStr}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Calendar exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export calendar');
    } finally {
      setExporting(false);
    }
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return tournaments.filter((t) => {
      return t.start_date <= dateStr && t.end_date >= dateStr;
    });
  };

  // Get all dates with events
  const eventDates = tournaments.reduce((dates, t) => {
    const start = new Date(t.start_date);
    const end = new Date(t.end_date);
    const current = new Date(start);
    
    while (current <= end) {
      dates.add(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }, new Set());

  const selectedDateEvents = getEventsForDate(selectedDate);

  // Upcoming events sorted by date - includes currently ongoing events
  const today = new Date().toISOString().split('T')[0];
  const upcomingEvents = tournaments
    .filter((t) => t.end_date >= today) // Show events that haven't ended yet
    .sort((a, b) => a.start_date.localeCompare(b.start_date));

  // Separate into ongoing and future events for display
  const ongoingEvents = upcomingEvents.filter((t) => t.start_date <= today && t.end_date >= today);
  const futureEvents = upcomingEvents.filter((t) => t.start_date > today);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600">Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Tournament Calendar</h1>
          <p className="text-slate-600 mt-1">
            View and manage your tournament schedule
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Archer filter for parents */}
          {isParent && familyArchers.length > 0 && (
            <Select value={filterArcherId} onValueChange={setFilterArcherId}>
              <SelectTrigger className="w-44" data-testid="calendar-archer-filter">
                <Users className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Filter by archer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Archers</SelectItem>
                {familyArchers.map((archer) => (
                  <SelectItem key={archer.id} value={archer.id}>
                    {archer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Button 
            variant="outline" 
            onClick={handleExportMonth}
            disabled={exporting}
            data-testid="export-calendar-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export {currentMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="card-base lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-primary" />
              Calendar View
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              className="rounded-md border w-full"
              modifiers={{
                hasEvent: (date) => eventDates.has(date.toISOString().split('T')[0])
              }}
              modifiersStyles={{
                hasEvent: {
                  fontWeight: 'bold',
                  backgroundColor: 'hsl(215 100% 45% / 0.1)',
                  color: 'hsl(215 100% 45%)'
                }
              }}
              data-testid="calendar-component"
            />
            <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary/20" />
                <span>Has events</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary" />
                <span>Selected</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Events Sidebar */}
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="text-lg">My Events</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Ongoing Events */}
            {ongoingEvents.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-2">
                  🟢 Happening Now ({ongoingEvents.length})
                </p>
                <div className="space-y-2">
                  {ongoingEvents.map((event) => {
                    const daysUntil = getDaysUntil(event.end_date);
                    return (
                      <div
                        key={event.id}
                        className="p-3 bg-green-50 border border-green-200 rounded-lg"
                        data-testid={`ongoing-event-${event.id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <Badge className={getOrgColor(event.org)} variant="secondary">
                            {event.org}
                          </Badge>
                          <Badge className={getStatusColor(event.tracking_status)}>
                            {event.tracking_status}
                          </Badge>
                        </div>
                        <h4 className="font-medium text-slate-900 text-sm truncate">
                          {event.name}
                        </h4>
                        {event.archer_name && (
                          <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700 border-purple-200 mt-1">
                            <User className="w-3 h-3 mr-1" />
                            {event.archer_name}
                          </Badge>
                        )}
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Ends {formatDate(event.end_date)}
                          {event.tracking_event_time && (
                            <span className="text-primary font-medium ml-1">
                              @ {event.tracking_event_time}
                            </span>
                          )}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Future Events */}
            {futureEvents.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">
                  📅 Upcoming ({futureEvents.length})
                </p>
                <div className="space-y-2">
                  {futureEvents.slice(0, 5).map((event) => {
                    const daysUntil = getDaysUntil(event.start_date);
                    return (
                      <div
                        key={event.id}
                        className="p-3 bg-slate-50 rounded-lg"
                        data-testid={`upcoming-event-${event.id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <Badge className={getOrgColor(event.org)} variant="secondary">
                            {event.org}
                          </Badge>
                          <Badge className={getStatusColor(event.tracking_status)}>
                            {event.tracking_status}
                          </Badge>
                        </div>
                        <h4 className="font-medium text-slate-900 text-sm truncate">
                          {event.name}
                        </h4>
                        {event.archer_name && (
                          <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700 border-purple-200 mt-1">
                            <User className="w-3 h-3 mr-1" />
                            {event.archer_name}
                          </Badge>
                        )}
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(event.start_date)}
                          {daysUntil !== null && daysUntil >= 0 && (
                            <span className="text-primary font-medium ml-1">
                              ({daysUntil === 0 ? 'Today' : `in ${daysUntil}d`})
                            </span>
                          )}
                        </p>
                      </div>
                    );
                  })}
                  {futureEvents.length > 5 && (
                    <p className="text-xs text-slate-500 text-center">
                      +{futureEvents.length - 5} more events
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Empty State */}
            {upcomingEvents.length === 0 && (
              <div className="text-center py-6 text-slate-500">
                <CalendarDays className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No upcoming events</p>
                <p className="text-xs mt-1">Track tournaments to see them here</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Selected Date Events */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-lg">
            Events on {selectedDate.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {selectedDateEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedDateEvents.map((event) => (
                <div
                  key={event.id}
                  className="p-4 bg-slate-50 rounded-lg"
                  data-testid={`selected-date-event-${event.id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={getOrgColor(event.org)}>{event.org}</Badge>
                    <Badge className={getStatusColor(event.tracking_status)}>
                      {event.tracking_status}
                    </Badge>
                    {event.archer_name && (
                      <Badge variant="secondary" className="bg-purple-100 text-purple-700 border-purple-200">
                        <User className="w-3 h-3 mr-1" />
                        {event.archer_name}
                      </Badge>
                    )}
                  </div>
                  <h4 className="font-semibold text-slate-900">{event.name}</h4>
                  <div className="mt-2 space-y-1 text-sm text-slate-500">
                    <p className="flex items-center gap-2">
                      <CalendarDays className="w-4 h-4" />
                      {formatDate(event.start_date)} - {formatDate(event.end_date)}
                    </p>
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {event.location}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <CalendarDays className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No events on this date</p>
              <p className="text-sm mt-1">
                Track tournaments to see them on your calendar
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
